import React, { useState, createContext, useContext, useCallback, type FC, type PropsWithChildren } from 'react';

export interface BaseGlobalModalProps {
  onClose: () => void;
}

interface GlobalModalContextShape {
  showModal: <P extends BaseGlobalModalProps>(
    ModalComponent: React.VFC<Omit<P, keyof BaseGlobalModalProps> & BaseGlobalModalProps>,
    modalProps: Omit<P, keyof BaseGlobalModalProps>,
  ) => void;
  hideModal: () => void;
}

const GlobalModalContext = createContext<GlobalModalContextShape | undefined>(undefined);

export const useGlobalModalContext = (): GlobalModalContextShape => {
  const value = useContext(GlobalModalContext);
  if (value === undefined) throw new Error('useGlobalModalContext must be used within a GlobalModalProvider');
  return value;
};

export const GlobalModal: FC<PropsWithChildren<object>> = ({ children }) => {
  const [element, setElement] = useState<JSX.Element | undefined>(undefined);

  const handleHideModal = useCallback(() => {
    setElement(undefined);
  }, []);

  const handleShowModal: GlobalModalContextShape['showModal'] = useCallback(
    (Component, props) => {
      setElement(<Component data-cy="global-modal" {...props} onClose={handleHideModal} />);
    },
    [setElement, handleHideModal],
  );

  const value = React.useMemo(
    () => ({ showModal: handleShowModal, hideModal: handleHideModal }),
    [handleHideModal, handleShowModal],
  );

  return (
    <GlobalModalContext.Provider value={value}>
      {element}
      {children}
    </GlobalModalContext.Provider>
  );
};
